import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { FiChevronRight } from "react-icons/fi";
import bedroom from "../../../assets/icon1.svg";
import shower from "../../../assets/icon.svg";
import squareMeters from "../../../assets/SquareMeters.svg";
import GoogleMap2 from "../GoogleMap/Googlemap2";
import isEmpty from "../utils/isEmpty";
import { CurrencySymbol } from "../../../utils/enums";

const FILTERS = [
  { label: "Sold", value: "Sold" },
  { label: "For sale", value: "Active" },
];

const AgentPropertiesCard = ({ AgentData, property }) => {
  const navigate = useNavigate();
  const [showListing, setShowListing] = useState(false);
  const [activeFilter, setActiveFilter] = useState("Active");

  const filteredProperties = useMemo(() => {
    if (!property || !Array.isArray(property)) return [];
    return property.filter(
      (item) => (item?.status || item?.status_type) === activeFilter,
    );
  }, [property, activeFilter]);

  const displayedCount = showListing
    ? filteredProperties.length
    : Math.min(3, filteredProperties.length);
  const totalCount = filteredProperties.length;
  const hasMore = totalCount > 3;

  const formatPrice = (item) => {
    if (item?.Hide_the_price_and_display_contact_agent) return "Contact agent";
    const p = item?.price ?? item?.display_price;
    if (p == null || p === "") return "–";
    if (typeof p === "number") return `${CurrencySymbol.NGN} ${p.toLocaleString()}`;
    return String(p)
      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
      .replace(/^/, `${CurrencySymbol.NGN}`);
  };

  const formatSoldDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getStatusLabel = (item) => {
    const status = item?.status ?? item?.status_type;
    const date = item?.sold_date ?? item?.createdAt ?? item?.updatedAt;
    if (status === "Sold")
      return date ? `Sold on ${formatSoldDate(date)}` : "Sold";
    if (status === "Active") return "For sale";
    if (status === "Rent") return "Leased";
    return status || "–";
  };

  const getArea = (item) => {
    const land = item?.land_size ?? item?.building_size ?? item?.living_area;
    if (land != null && land !== "") return `${land} m²`;
    return "–";
  };

  const propertyImg = (item) => {
    const raw = item?.frontPageImg ?? item?.florePlansImg ?? item?.images;
    return Array.isArray(raw) ? raw[0] : raw;
  };

  const bathroomsDisplay = (item) => {
    const b = item?.Bathrooms;
    if (b == null) return "–";
    if (typeof b === "string" && b.includes("+")) return b.split("+")[1] ?? b;
    return b;
  };

  const showMoreLessListings = () => {
    setShowListing((prev) => !prev);
    if (showListing) {
      const el = document.querySelector(".data");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const resPonce = isEmpty(property);
  const agentName =
    AgentData?.first_name ?? AgentData?.data?.first_name ?? "Agent";

  return (
    <div
      className="data flex flex-col bg-white rounded-xl overflow-hidden"
      style={{ boxShadow: "0px 1px 4px 0px rgba(0, 0, 0, 0.16)" }}
    >
      <div className="p-4 md:p-5 md:pb-4 flex flex-col md:flex-row justify-between items-start md:items-center">
        <div>
          <h3 className="text-[#171717] font-bold text-xl md:text-2xl">
            {agentName}'s properties
          </h3>
          <p className="text-[#737373] text-sm mt-1">
            Showing all for sale and sold listings in and around these suburbs.
          </p>
        </div>
        <div className="inline-flex rounded-full bg-[#F3F6F9] p-1 mt-4 ">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setActiveFilter(f.value)}
              className={`rounded-full px-4 py-2 text-sm transition-colors ${activeFilter === f.value
                ? "bg-white text-black font-medium "
                : "text-[#525252] hover:text-[#171717]"
                }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="relative rounded-b-none overflow-hidden">
        {!resPonce && filteredProperties.length > 0 && (
          <GoogleMap2
            key={`${activeFilter}-${filteredProperties.length}`}
            location={filteredProperties}
          />
        )}
        {!resPonce && filteredProperties.length === 0 && (
          <div className="h-[400px] flex items-center justify-center bg-[#F3F6F9] text-[#737373] text-sm">
            No {activeFilter === "Active" ? "for sale" : "sold"} properties to show on the map.
          </div>
        )}
      </div>

      <div className="p-4 md:p-5">
        <p className="text-[#737373] text-sm">
          Showing {displayedCount} of {totalCount}{" "}
          {activeFilter === "Active" ? "for sale" : "sold"} properties.
        </p>

        <div className="mt-4 flex flex-col gap-4">
          {(showListing
            ? filteredProperties
            : filteredProperties.slice(0, 3)
          ).map((i, index) => (
            <div
              key={i?.id ?? i?._id ?? index}
              className="relative flex flex-col md:flex-row md:gap-4 cursor-pointer border-[1.5px] border-[#EAEAEA] rounded-xl"
              onClick={() => navigate(`/property-house/${i?._id ?? i?.id}`)}
            >
              <LazyLoadImage
                src={propertyImg(i)}
                alt=""
                loading="lazy"
                effect="blur"
                className="rounded-tl-xl rounded-tr-xl md:rounded-l-xl md:rounded-r-none h-[210px] w-full md:h-[210px] md:w-[251px] md:max-w-[250px] object-cover"
              />
              <div className="absolute max-[767px]:top-4 md:bottom-2 left-2 bg-white p-1 rounded-lg flex items-center gap-1">
                <div className="w-2 h-2 p-1 rounded-full bg-[#FCCA36]" />
                <span className="text-xs font-normal">{getStatusLabel(i)}</span>
              </div>
              <div className="py-5 px-3 w-full mt-2 flex flex-col gap-3">
                <div className="flex justify-between items-center">
                  <div className="font-bold text-2xl text-[#097FE6]">
                    {formatPrice(i)}
                  </div>
                  <div className="font-semibold text-sm text-[#000929] border-[1.5px] border-[#EAEAEA] rounded-full py-1 px-2 flex items-center gap-1">
                    <div className="w-[7px] h-[7px] rounded-full bg-[#097FE6]" />
                    {i?.createdAt
                      ? `Sold on ${formatSoldDate(i.createdAt)}`
                      : getStatusLabel(i)}
                  </div>
                </div>
                <div className="flex justify-start items-start">
                  <div className="font-medium text-[#000929] text-xl capitalize">
                    {i?.name ||
                      `${i?.street_address_number || ""} ${i?.street_address_name || ""}`.trim() ||
                      "Property"}
                  </div>
                </div>
                <div className="flex justify-start items-start">
                  <div className="font-normal text-[#000929] text-sm opacity-50">
                    {i?.street_address_number} {i?.street_address_name}
                    {i?.suburb ? `, ${i.suburb}` : ""}
                  </div>
                </div>
                <div className="flex flex-wrap justify-start items-center gap-3">
                  <div className="flex justify-center items-center rounded-md">
                    <img src={bedroom} alt="" className="w-4 mr-2" />
                    <div className="font-medium text-sm text-[#000929] opacity-75">
                      {i?.Bedrooms ?? "–"} Beds
                    </div>
                  </div>
                  <div className="flex justify-center items-center rounded-md">
                    <img src={shower} alt="" className="w-4 mr-2" />
                    <div className="font-medium text-sm text-[#000929] opacity-75">
                      {bathroomsDisplay(i)} Bathrooms
                    </div>
                  </div>
                  <div className="flex justify-center items-center rounded-md">
                    <img src={squareMeters} alt="" className="w-4 mr-2" />
                    <div className="font-medium text-sm text-[#000929] opacity-75">
                      {i?.land_size || i?.house_size || "–"} m²
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {hasMore && (
          <div className="flex justify-center mt-6">
            <button
              type="button"
              onClick={showMoreLessListings}
              className="border border-[#E5E5E5] rounded-full bg-white text-[#525252] text-sm font-medium px-6 py-3 hover:bg-[#FAFAFA] transition-colors"
            >
              {showListing ? "Show less listings" : "Show more listings"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentPropertiesCard;
